import LoadingSpinner from '@common/src/components/LoadingSpinner';
import { AnvaErrorAlert } from '@flows/src/components/flow-products/quotes/basket/actions/anva-party-creation-modal/AnvaErrorAlert';
import { AnvaPartyCreationSchema } from '@flows/src/types/AnvaPartyCreation';
import { yupResolver } from '@hookform/resolvers/yup';
import { usePostAnvaParty } from '@parties/src/api/postAnvaParty';
import { Party } from '@parties/src/types';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Text,
  Collapse,
} from '@wegroup/design-system';
import cogoToast from 'cogo-toast';
import { FC, lazy, Suspense } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const AnvaPartyCreationLegalForm = lazy(
  () =>
    import(
      '@flows/src/components/flow-products/quotes/basket/actions/anva-party-creation-modal/AnvaPartyCreationLegalForm'
    ),
);
const AnvaPartyCreationCustomerForm = lazy(
  () =>
    import(
      '@flows/src/components/flow-products/quotes/basket/actions/anva-party-creation-modal/AnvaPartyCreationCustomerForm'
    ),
);

interface Props {
  party: Party;
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
  onFinish: () => void;
}

const getDefaultValues = (party: Party) => {
  const companyDetails = {
    name: party.name,
    company_registration: party.company_registration,
    legal_form: party.legal_form,
    address: party.address,
  };

  const customerDetails = {
    initials: party.initials,
    infix: party.infix,
    last_name: party.last_name,
    address: party.address,
  };

  return {
    type: party.type,
    details: party.type === 'CUSTOMER' ? customerDetails : companyDetails,
  };
};

export const FinishBasketPartyCreationModal: FC<Props> = ({
  party,
  sessionId,
  isOpen,
  onClose,
  onFinish,
}) => {
  const { t } = useTranslation();
  const { mutateAsync: createAnvaParty, isError, error } = usePostAnvaParty();
  const methods = useForm({
    defaultValues: getDefaultValues(party),
    resolver: yupResolver(AnvaPartyCreationSchema),
  });

  const handleSubmit = methods.handleSubmit(async (data) => {
    try {
      await createAnvaParty({
        type: party.type,
        sessionId,
        payload: { id: party.id, ...data.details },
      });
      methods.reset({
        type: party?.type,
        details: data.details,
      });

      cogoToast.success(t('PORTFOLIO_PARTY-DETAIL_PARTY-EDITED'));
      onClose();
      onFinish();
    } catch (err) {
      console.error('Error creating Anva party:', err);
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <FormProvider {...methods}>
        <ModalContent>
          <ModalHeader>
            <Flex justify="space-between" pr="8" align="center">
              <Text>{t(`ANVA_FLOWS_CREATE_PARTY_MODAL_TITLE`)}</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton mt="2" />
          <ModalBody>
            <Collapse in={isError}>
              <AnvaErrorAlert error={error as Error} />
            </Collapse>
            <Suspense fallback={<LoadingSpinner />}>
              {party?.type === 'CUSTOMER' ? (
                <AnvaPartyCreationCustomerForm />
              ) : (
                <AnvaPartyCreationLegalForm />
              )}
            </Suspense>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('CLOSE')}
            </Button>
            <Button
              isDisabled={!methods.formState.isDirty}
              onClick={handleSubmit}
              isLoading={methods.formState.isSubmitting}
            >
              {t('ANVA_FLOWS_CREATE_PARTY_MODAL_SUBMIT')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </FormProvider>
    </Modal>
  );
};
